/**
 * Evaluador simple de fortaleza de contraseña (panel admin).
 * Mismo algoritmo que el mobile — duplicado para mantener libs
 * independientes. Si en el futuro se extrae a un paquete compartido,
 * reemplazar.
 */

export type PasswordStrength = {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  /** Tailwind-friendly color name para barras. */
  colorClass: string;
  feedback: string[];
  acceptable: boolean;
};

const LABELS = ["Muy débil", "Débil", "Regular", "Buena", "Fuerte"];
const COLOR_CLASSES = [
  "bg-red-600",
  "bg-orange-500",
  "bg-yellow-400",
  "bg-emerald-500",
  "bg-emerald-700",
];

export function passwordStrength(password: string): PasswordStrength {
  const feedback: string[] = [];
  let score = 0;

  if (password.length < 8) feedback.push("Usa al menos 8 caracteres");
  else score++;

  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  else feedback.push("Combina mayúsculas y minúsculas");

  if (/\d/.test(password)) score++;
  else feedback.push("Incluye al menos un número");

  if (/[^\w\s]/.test(password)) score++;
  else feedback.push("Agrega un símbolo (ej. !, @, #)");

  if (password.length < 6) score = 0;
  if (/^(.)\1+$/.test(password)) score = 0;
  if (/^(012|123|234|345|456|567|678|789|890|abc|qwe|pas)/i.test(password)) {
    score = Math.min(score, 1);
  }

  const clampedScore = Math.max(0, Math.min(4, score)) as 0 | 1 | 2 | 3 | 4;

  return {
    score: clampedScore,
    label: LABELS[clampedScore],
    colorClass: COLOR_CLASSES[clampedScore],
    feedback,
    acceptable: clampedScore >= 2 && password.length >= 8,
  };
}

/**
 * Traduce errores de Supabase Auth a mensajes amigables en español.
 */
export function formatAuthError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("pwned") || m.includes("leaked") || m.includes("compromised")) {
    return "Esta contraseña aparece en filtraciones públicas. Usa otra más segura.";
  }
  if (m.includes("weak") || m.includes("password should")) {
    return "La contraseña es demasiado débil. Combina mayúsculas, números y símbolos.";
  }
  return message;
}
